import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { CheckCircle, Circle, Star, Loader, BookOpen, RefreshCw, Pen, HelpCircle, Zap, Heart, ChevronDown, ChevronUp, Video, Gift, Users, Music, MessageCircle, Sparkles } from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";
import { BottomNav } from "./ChildDashboard";
import { useLanguage } from "../hooks/useLanguage";
import DrawingCanvas from "./DrawingCanvas";
import AnswerBox from "./AnswerBox";

export default function TasksPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [child, setChild]       = useState(null);
  const [taskSet, setTaskSet]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [completing, setCompleting] = useState(null);
  const [error, setError]       = useState("");
  const [expandedTask, setExpandedTask] = useState(null);
  const [taskResponses, setTaskResponses] = useState({});

  // Activity type configurations with icons and colors
  const TYPE_CONFIG = {
    // Original types
    question:  { icon: HelpCircle, grad: ["#118AB2","#4CC9F0"], label: t('question')  },
    journal:   { icon: Pen,        grad: ["#F77F00","#FFD166"], label: t('journal')   },
    challenge: { icon: Zap,        grad: ["#9B5DE5","#EF476F"], label: t('challenge') },
    prayer:    { icon: Heart,      grad: ["#EF476F","#FFD166"], label: t('prayer')    },
    // Deep Dive & Discussion (Monday)
    deep_study:   { icon: BookOpen,      grad: ["#073B4C","#118AB2"], label: "Deep Dive" },
    discussion:   { icon: MessageCircle, grad: ["#06D6A0","#118AB2"], label: "Discussion" },
    reflection:   { icon: Pen,           grad: ["#9B5DE5","#118AB2"], label: "Reflection" },
    // Creative Expression (Tuesday)
    song_writing:  { icon: Music,    grad: ["#EF476F","#FFD166"], label: "Write a Song" },
    art_project:   { icon: Sparkles, grad: ["#F77F00","#FFD166"], label: "Art Project" },
    letter_writing:{ icon: Pen,      grad: ["#118AB2","#06D6A0"], label: "Write a Letter" },
    // Family Play (Wednesday)
    roleplay:     { icon: Users, grad: ["#9B5DE5","#EF476F"], label: "Act It Out" },
    family_skit:  { icon: Users, grad: ["#EF476F","#F77F00"], label: "Family Play" },
    charades:     { icon: Zap,   grad: ["#FFD166","#F77F00"], label: "Charades" },
    // Giving & Service (Thursday)
    giving_challenge: { icon: Gift,  grad: ["#06D6A0","#118AB2"], label: "Give to Others" },
    service_project:  { icon: Heart, grad: ["#EF476F","#9B5DE5"], label: "Help Someone" },
    prayer_for_others:{ icon: Heart, grad: ["#9B5DE5","#EF476F"], label: "Pray for Others" },
    // Watch & Learn (Friday)
    watch_video:    { icon: Video,         grad: ["#118AB2","#06D6A0"], label: "Watch Video" },
    video_response: { icon: MessageCircle, grad: ["#F77F00","#FFD166"], label: "Video Response" },
    share_learning: { icon: Users,         grad: ["#06D6A0","#118AB2"], label: "Share Learning" },
    // Application (Saturday)
    thankful_jar:     { icon: Gift,     grad: ["#FFD166","#F77F00"], label: "Thankful Jar" },
    practical_step:   { icon: Zap,      grad: ["#118AB2","#073B4C"], label: "Take a Step" },
    memory_challenge: { icon: BookOpen, grad: ["#9B5DE5","#118AB2"], label: "Memorize" },
    // Worship (Sunday)
    worship_activity: { icon: Music,    grad: ["#EF476F","#FFD166"], label: "Worship" },
    gratitude_list:   { icon: Heart,    grad: ["#06D6A0","#FFD166"], label: "Gratitude" },
    family_prayer:    { icon: Heart,    grad: ["#9B5DE5","#EF476F"], label: "Lead Prayer" },
  };

  useEffect(() => { loadTasks(); }, [childId]);

  const loadTasks = async () => {
    setLoading(true); setError("");
    try {
      const [cr, tr] = await Promise.all([
        axios.get(`${API}/children/${childId}`),
        axios.get(`${API}/tasks/${childId}`),
      ]);
      setChild(cr.data); setTaskSet(tr.data);
      
      // Load existing task responses
      if (tr.data?.id) {
        try {
          const respRes = await axios.get(`${API}/tasks/${tr.data.id}/responses/${childId}`);
          const respMap = {};
          (respRes.data || []).forEach(r => {
            respMap[r.task_index] = r;
          });
          setTaskResponses(respMap);
        } catch {}
      }
    } catch (e) { setError(e.response?.data?.detail || "Could not load tasks."); }
    finally { setLoading(false); }
  };

  const handleComplete = async (idx) => {
    if (!taskSet || completing !== null || taskSet.tasks[idx]?.completed) return;
    setCompleting(idx);
    try {
      const { data } = await axios.post(`${API}/tasks/${taskSet.id}/complete/${idx}`);
      setTaskSet(data);
    } catch (e) { console.error(e); }
    finally { setCompleting(null); }
  };

  const saveTaskDrawing = async (dataUrl, taskIndex) => {
    try {
      await axios.post(`${API}/tasks/${taskSet.id}/drawing`, {
        child_id: childId,
        task_index: taskIndex,
        drawing_data: dataUrl
      });
      // Update local state
      setTaskResponses(prev => ({
        ...prev,
        [taskIndex]: { ...prev[taskIndex], drawing_data: dataUrl }
      }));
    } catch (e) {
      console.error("Failed to save task drawing:", e);
    }
  };

  const saveTaskAnswer = async (content, taskIndex) => {
    try {
      await axios.post(`${API}/tasks/${taskSet.id}/answer`, {
        child_id: childId,
        task_index: taskIndex,
        content: content
      });
      // Update local state
      setTaskResponses(prev => ({
        ...prev,
        [taskIndex]: { ...prev[taskIndex], answer_content: content }
      }));
    } catch (e) {
      console.error("Failed to save task answer:", e);
    }
  };

  const toggleExpand = (idx) => {
    setExpandedTask(expandedTask === idx ? null : idx);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pb-20" style={{ background: "#F0F4FF" }}>
      <div className="text-center px-4">
        <Loader size={52} color="#9B5DE5" className="mx-auto animate-spin mb-4" />
        <p className="font-black text-xl text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
          {t('creating_activities')}
        </p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center pb-20 px-4" style={{ background: "#F0F4FF" }}>
      <div className="text-center max-w-sm bg-white rounded-3xl p-8 shadow-lg">
        <BookOpen size={44} color="#118AB2" className="mx-auto mb-4" strokeWidth={2} />
        <p className="text-gray-700 font-bold mb-2">{t('read_lesson_first')}</p>
        <p className="text-gray-500 text-sm mb-5">{error}</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate(`/child/${childId}/lesson`)}
            className="text-white font-bold px-5 py-3 rounded-xl text-sm"
            style={{ background: "#118AB2" }} data-testid="go-to-lesson-btn">{t('go_to_lesson')}</button>
          <button onClick={loadTasks}
            className="flex items-center gap-2 bg-gray-100 text-gray-600 font-bold px-5 py-3 rounded-xl text-sm"
            data-testid="retry-tasks-btn"><RefreshCw size={14} /> {t('retry')}</button>
        </div>
      </div>
      <BottomNav childId={childId} active="tasks" />
    </div>
  );

  const tasks = taskSet?.tasks || [];
  const completedCount = tasks.filter(t => t.completed).length;
  const allDone = completedCount === tasks.length && tasks.length > 0;

  return (
    <div className="min-h-screen pb-24" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-10"
        style={{ background: "linear-gradient(135deg, #9B5DE5 0%, #EF476F 100%)" }}>
        <div className="max-w-lg mx-auto flex items-center gap-3 mb-5">
          {child && <AvatarDisplay avatar={child.avatar} size={44} />}
          <div>
            <p className="text-white/70 text-xs font-medium">{t('activities_for')}</p>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {child?.name}
            </h1>
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-lg mx-auto">
          <div className="flex justify-between mb-1">
            <span className="text-white/80 text-sm font-bold">{completedCount} {t('of_done')} {tasks.length} {t('done')}</span>
            <span className="text-white font-black text-sm">+{completedCount} {t('stars').toLowerCase()}</span>
          </div>
          <div className="h-4 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-[#FFD166] rounded-full transition-all duration-700"
              style={{ width: tasks.length > 0 ? `${(completedCount/tasks.length)*100}%` : "0%" }} />
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-3">
        {/* All done */}
        {allDone && (
          <div className="rounded-3xl p-5 mb-4 text-center border-b-4"
            style={{ background: "linear-gradient(135deg, #FFD166, #F77F00)", borderColor: "#e0b040" }}>
            <Star size={40} color="white" fill="white" className="mx-auto mb-2 sparkle" strokeWidth={0} />
            <p className="font-black text-xl text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('all_done_amazing')}
            </p>
            <p className="text-white/80 text-sm mt-1">{t('earned_stars_today').replace('{count}', tasks.length)}</p>
          </div>
        )}

        {/* Task cards */}
        <div className="space-y-4">
          {tasks.map((task, i) => {
            const typeKey = task.type?.toLowerCase() || "challenge";
            const cfg = TYPE_CONFIG[typeKey] || TYPE_CONFIG.challenge;
            const Icon = cfg.icon;
            const isCompleting = completing === i;
            const isExpanded = expandedTask === i;
            const taskResponse = taskResponses[i] || {};
            
            return (
              <div key={task.id || i}
                className={`bg-white rounded-3xl overflow-hidden shadow-md transition-all duration-300 ${
                  task.completed ? 'ring-2 ring-[#06D6A0]' : ''
                }`}
                data-testid={`task-card-${i}`}>
                {/* Color strip */}
                <div className="h-2" style={{ background: `linear-gradient(90deg, ${cfg.grad[0]}, ${cfg.grad[1]})` }} />
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="rounded-2xl p-3 flex-shrink-0"
                      style={{ background: `linear-gradient(135deg, ${cfg.grad[0]}, ${cfg.grad[1]})` }}>
                      <Icon size={22} color="white" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-black px-2 py-0.5 rounded-full text-white"
                          style={{ background: cfg.grad[0] }}>{cfg.label}</span>
                        <div className="flex gap-0.5">
                          {Array.from({ length: task.stars||1 }).map((_,j) => (
                            <Star key={j} size={12} color="#FFD166" fill="#FFD166" strokeWidth={0} />
                          ))}
                        </div>
                      </div>
                      <h3 className={`font-black text-base text-[#073B4C] mb-1 ${task.completed?"line-through opacity-50":""}`}
                        style={{ fontFamily: "Fredoka, sans-serif" }}>{task.title}</h3>
                      <p className={`text-sm text-gray-600 leading-relaxed ${task.completed?"opacity-50":""}`}
                        style={{ fontFamily: "Quicksand, sans-serif" }}>
                        {/* Parse description for YouTube links and make them clickable */}
                        {task.description?.includes('youtube.com') || task.description?.includes('youtu.be') ? (
                          <>
                            {task.description.split(/(https?:\/\/[^\s]+)/g).map((part, idx) => 
                              part.match(/https?:\/\/[^\s]+/) ? (
                                <a 
                                  key={idx}
                                  href={part}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#118AB2] font-bold underline hover:text-[#06D6A0] inline-flex items-center gap-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  📺 Watch Video
                                </a>
                              ) : part
                            )}
                          </>
                        ) : task.description}
                      </p>
                    </div>
                    <button onClick={() => handleComplete(i)}
                      disabled={task.completed || isCompleting}
                      className="flex-shrink-0 mt-1 transition-all hover:scale-110 active:scale-95"
                      data-testid={`complete-task-${i}`}>
                      {isCompleting ? <Loader size={32} color={cfg.grad[0]} className="animate-spin" />
                        : task.completed ? <CheckCircle size={32} color="#06D6A0" strokeWidth={2} />
                        : <Circle size={32} color="#D1D5DB" strokeWidth={2} />}
                    </button>
                  </div>
                  
                  {/* Expand/Collapse button for writing & drawing */}
                  {!task.completed && (
                    <button 
                      onClick={() => toggleExpand(i)}
                      className="w-full mt-4 flex items-center justify-center gap-2 py-2 rounded-xl bg-gray-100 text-gray-600 font-bold text-sm hover:bg-gray-200 transition-all"
                      data-testid={`expand-task-${i}`}
                    >
                      {isExpanded ? (
                        <>Hide Write & Draw <ChevronUp size={16} /></>
                      ) : (
                        <>Write & Draw <ChevronDown size={16} /></>
                      )}
                    </button>
                  )}
                  
                  {/* Expanded section with writing and drawing */}
                  {isExpanded && !task.completed && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                      {/* Writing section */}
                      <AnswerBox
                        prompt={`Write about: ${task.title}`}
                        placeholder="Write your thoughts, prayer, or answer here..."
                        onSave={(content) => saveTaskAnswer(content, i)}
                        savedAnswer={taskResponse.answer_content}
                        promptIndex={i}
                        maxLength={300}
                      />
                      
                      {/* Drawing section */}
                      <DrawingCanvas
                        title="Draw Your Response!"
                        height={180}
                        onSave={(dataUrl) => saveTaskDrawing(dataUrl, i)}
                        savedDrawing={taskResponse.drawing_data}
                        activityType={`task_${i}`}
                      />
                    </div>
                  )}
                  
                  {/* Show saved responses for completed tasks */}
                  {task.completed && (taskResponse.answer_content || taskResponse.drawing_data) && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      {taskResponse.answer_content && (
                        <div className="bg-green-50 rounded-xl p-3 mb-3">
                          <p className="text-xs font-bold text-green-600 mb-1">Your Answer:</p>
                          <p className="text-sm text-gray-700">"{taskResponse.answer_content}"</p>
                        </div>
                      )}
                      {taskResponse.drawing_data && (
                        <div className="bg-purple-50 rounded-xl p-3">
                          <p className="text-xs font-bold text-purple-600 mb-2">Your Drawing:</p>
                          <img src={taskResponse.drawing_data} alt="Your drawing" className="w-full rounded-lg" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {allDone && (
          <button onClick={() => navigate(`/child/${childId}/rewards`)}
            className="w-full mt-5 text-white font-black py-4 rounded-2xl border-b-4 active:border-b-0 active:translate-y-1 transition-all text-lg"
            style={{ fontFamily: "Fredoka, sans-serif", background: "linear-gradient(90deg, #EF476F, #FFD166)",
              borderColor: "#c8375a" }}
            data-testid="see-rewards-btn">
            {t('see_my_rewards')}
          </button>
        )}
      </div>
      <BottomNav childId={childId} active="tasks" />
    </div>
  );
}
